import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChargeBeePlanId, isEnterprise } from '@prerender/billing-shared';

import RequestUpgradeFromAdminsModal from './RequestUpgradeFromAdminsModal';
import { useUserRole, usePrerenderEmail, useSelector } from '../../hooks/usePrerenderUser';
import { UserRole } from '../PrerenderUser';
import { URLFromParams } from '../../utils/UrlFromParams';
import { HubSpot } from '~features/CustomerSupport';
import { useTrackBillingEvent } from '~features/billing';

type OnUpgradePlanCallback = (
  from?: URLFromParams,
  planId?: ChargeBeePlanId,
  trackingProperties?: {
    ctaLocation: string;
    ctaName: string;
  }
) => void;
interface UpgradePlanContextValue {
  openUpgradePlan: OnUpgradePlanCallback;
}

const UpgradePlanContext = createContext<UpgradePlanContextValue | undefined>(undefined);

interface UpgradePlanProviderProps {
  children: ReactNode;
}

export const UpgradePlanProvider: React.FC<UpgradePlanProviderProps> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const userFirstName = useSelector((state) => state.prerenderUser.firstName);
  const userEmail = usePrerenderEmail();
  const hubspotLink = HubSpot.createFormUrl(userFirstName || '', userEmail);
  const userRole: UserRole = useUserRole();
  const trackBillingEvent = useTrackBillingEvent();

  const isManager =
    userRole === UserRole.Admin || userRole === UserRole.SuperAdmin || userRole === UserRole.BillingManager;

  const openUpgradePlan: OnUpgradePlanCallback = (from, planId, trackingProperties) => {
    trackBillingEvent('UpgradeClicked', {
      cta_location: trackingProperties?.ctaLocation || from || '',
      cta_name: trackingProperties?.ctaName || 'Upgrade',
    });

    if (isManager) {
      if (planId && isEnterprise(planId)) {
        window.open(hubspotLink, '_blank');
        return;
      }

      const url = new URL('/billing/plans', window.location.origin);

      if (from) url.searchParams.set('from', from);
      if (planId) url.searchParams.set('selectedPlan', planId);

      navigate(url.pathname + url.search);
      return;
    }
    setIsModalOpen(true);
  };

  const contextValue: UpgradePlanContextValue = {
    openUpgradePlan,
  };

  return (
    <UpgradePlanContext.Provider value={contextValue}>
      {children}
      <RequestUpgradeFromAdminsModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </UpgradePlanContext.Provider>
  );
};

export const useUpgradePlan = (): UpgradePlanContextValue => {
  const context = useContext(UpgradePlanContext);
  if (context === undefined) {
    throw new Error('useUpgradePlan must be used within an UpgradePlanProvider');
  }
  return context;
};

export default UpgradePlanProvider;
